/* eslint-disable @typescript-eslint/no-explicit-any */
import { Pathname } from "./pathname";

export const PathnameGenerator = (name: string): string => Pathname.DASHBOARD_RENTAL + name;

const CRUD_ACTIONS = { ADD: 'add', VIEW: 'view', EDIT: 'edit' } as const;
type CrudAction = keyof typeof CRUD_ACTIONS;

type ModulePaths = Record<string, string>;

export const MAIN_MENU: ModulePaths = {
  PRODUCT_MANAGEMENT: 'product-management',
  PURCHASE: 'purchase',
  CROSS_HIRE: 'cross-hire',
  SETTINGS: 'settings',
  REPORTS: 'reports',
};

export const MODULES: ModulePaths = {
  DEMAND_PLANNING: 'demand-planning',
  ITEM: 'item',
  CATEGORY: 'category',
  INVOICING: 'invoicing',
  LEADS: 'leads',
  OPPORTUNITY: 'opportunity',
  QUOTATION: 'quotation',
  RENTAL_ORDERS: 'orders',
  RENTAL_DELIVERY_ORDERS: `orders/:rentalOrderId/delivery-orders`,
  RENTAL_GRNS: 'orders/:rentalOrderId/delivery-orders/:deliveryOrderId/grns',
  AGREEMENTS: 'agreements',
  PURCHASE_REQUEST_FOR_QUOTE: 'request-for-quote',
  PURCHASE_ORDERS: 'orders',
  CROSS_HIRE_REQUESTS: 'requests',
  CROSS_HIRE_RFQ: 'request-for-quote',
  CROSS_HIRE_ORDERS: 'orders',
  CROSS_HIRE_ORDERS_GRNS: 'orders/:id/grns',
  CROSS_HIRE_DELIVERY_ORDERS: 'orders/:id/delivery-orders',
  SETTINGS: 'settings',
  CONFIGURATIONS: 'configurations',
  BILLING_CYCLE: 'billing-cycle',
  TERMS_AND_CONDITIONS: 'terms-and-conditions',
  CROSS_HIRE_OVERVIEW: 'cross-hire-overview',
  CROSS_HIRE_PROCESS: 'process',
};

export const generateCrudPaths = (
  base: string,
  entity: string,
  prefix = '',
  keyPostfix = ''
) =>
  { 
    return Object.fromEntries(
    Object.entries(CRUD_ACTIONS).map(([key, action]) => {
      const isEditOrView = action === 'edit' || action === 'view';
      const id = (isEditOrView && MODULES[base]?.includes(':id')) ? `:${entity?.replace('-', "")}Id` : ':id'
      const routeParts = [prefix, MODULES[base]];
      if (isEditOrView) {
       routeParts.push(id, `${action}-${entity}`);
      } else {
        routeParts.push(`${action}-${entity}`);
      }

      return [
        `${key}_${keyPostfix.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase()}`,
        `/${routeParts.filter(Boolean).join('/')}`,
      ];
    })
  );
}


export const generateModulePaths = (mainModule: keyof typeof MAIN_MENU, subModule: keyof typeof MODULES = '', entity: string, keyPostfix = '') => ({
  [subModule.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase()]: `/${[MAIN_MENU[mainModule], MODULES[subModule]].filter(Boolean).join('/')}`,
  ...generateCrudPaths(subModule, entity, MAIN_MENU[mainModule], keyPostfix),
});

const leadPaths ={
  ...generateModulePaths('', 'LEADS', 'lead', 'leads'),
  LEADS_CALENDAR:`/${MODULES.LEADS}/lead-calendar`
};
const opportunityPaths = {
  ...generateModulePaths('', 'OPPORTUNITY', 'opportunity', 'OPPORTUNITY'),
  VIEW_SALES_PIPELINE: `/${MAIN_MENU.OPPORTUNITY}/${MODULES.OPPORTUNITY}/view-sales-pipeline`,
  OPPORTUNITY_CALENDAR: `/${MODULES.OPPORTUNITY}/calendar`,
}
const quotationPaths = {
  ...generateModulePaths('', 'QUOTATION', 'quotation', 'rental-quotation'),
  REVISED_QUOTATION:`/${MODULES.QUOTATION}/revised-quotation`
};
const rentalPaths = {
  ...generateModulePaths('', 'RENTAL_ORDERS', 'order', 'rental-order'),
  VIEW_INVOICE: `/${MODULES.RENTAL_ORDERS}/view-invoice`,
  GRNS: `/${MODULES.RENTAL_ORDERS}/grns`,
};
const rentalDeliveryPaths = generateModulePaths('RENTAL_ORDERS', 'RENTAL_DELIVERY_ORDERS', 'delivery-order', 'rental-delivery-order');
const rentalGrnPaths = generateModulePaths('RENTAL_ORDERS', 'RENTAL_GRNS', 'grn', 'rental-grn');
const agreementsPaths = generateModulePaths('', 'AGREEMENTS', 'agreement', 'agreement');
const rfqPaths = {
  ...generateModulePaths('PURCHASE', 'PURCHASE_REQUEST_FOR_QUOTE', 'request-for-quote', 'purchase-request-for-quote'),
  RFQ_VIEW_ANALYZE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_REQUEST_FOR_QUOTE}/analyze`,
  RFQ_RESPONSE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_REQUEST_FOR_QUOTE}/response`,
  RFQ_VIEW_RESPONSE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_REQUEST_FOR_QUOTE}/response/view-response`,
  RFQ_ADD_RESPONSE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_REQUEST_FOR_QUOTE}/response/add-response`,
  RFQ_EDIT_RESPONSE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_REQUEST_FOR_QUOTE}/response/edit-response`,
}
const purchaseOrderPaths = {
  ...generateModulePaths('PURCHASE', 'PURCHASE_ORDERS', 'order', 'purchase-order'),
  ...generateCrudPaths('PURCHASE_ORDERS', 'goods-receipt-note', MAIN_MENU.PURCHASE, 'goods-receipt-note'),
  GOODS_RECEIPT_NOTE: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_ORDERS}/goods-receipt-note`,
  BILLS: `/${MAIN_MENU.PURCHASE}/${MODULES.PURCHASE_ORDERS}/bills`,
};
const crossHireRequestPaths = generateModulePaths('CROSS_HIRE', 'CROSS_HIRE_REQUESTS', 'request', 'cross-hire-request');
const crossHireRfqPaths = {
  ...generateModulePaths('CROSS_HIRE', 'CROSS_HIRE_RFQ', 'request-for-quote', 'cross-hire-request-for-quote'),
  ...generateCrudPaths('CROSS_HIRE', 'cross-hire-response', 'response', 'cross-hire-response'),
  CROSS_HIRE_RESPONSES: `/${MAIN_MENU.CROSS_HIRE}/${MODULES.CROSS_HIRE_RFQ}/responses`,
  VIEW_ANALYZE: `/${MAIN_MENU.CROSS_HIRE}/${MODULES.CROSS_HIRE_RFQ}/analyze`,

}
const crossHireOrdersPaths = generateModulePaths('CROSS_HIRE', 'CROSS_HIRE_ORDERS', 'order', 'cross-hire-order');
const crossHireOrdersGrnPaths = generateModulePaths('CROSS_HIRE', 'CROSS_HIRE_ORDERS_GRNS', 'grn', 'cross-hire-order-grn');
const crossHireDeliveryOrdersPaths = generateModulePaths('CROSS_HIRE', 'CROSS_HIRE_DELIVERY_ORDERS', 'delivery-order', 'cross-hire-delivery-order');
const crossHireProcessPaths = { CROSS_HIRE_PROCESS: `/${MAIN_MENU.CROSS_HIRE}/${MODULES.CROSS_HIRE_PROCESS}` };
const settingPaths = {
  CONFIGURATIONS: `/${MAIN_MENU.SETTINGS}/${MODULES.CONFIGURATIONS}`,
  BILLING_CYCLE: `/${MAIN_MENU.SETTINGS}/${MODULES.BILLING_CYCLE}`,
  ...generateCrudPaths('BILLING_CYCLE', 'billing-cycle', MAIN_MENU.SETTINGS, 'billing-cycle'),
  TERMS_AND_CONDITIONS: `/${MAIN_MENU.SETTINGS}/${MODULES.TERMS_AND_CONDITIONS}`,
  ...generateCrudPaths('TERMS_AND_CONDITIONS', 'terms-and-conditions', MAIN_MENU.SETTINGS, 'terms-and-conditions'),
};
const reportsPaths = { 
  CROSS_HIRE_OVERVIEW: `/${MAIN_MENU.REPORTS}/${MODULES.CROSS_HIRE_OVERVIEW}`,
  RENTAL_PAYMENT_AGING_REPORT: `/${MAIN_MENU.REPORTS}/rental-payment-aging-report`,
  RENTAL_ASSET_RETURN_REPORT: `/${MAIN_MENU.REPORTS}/rental-asset-return-report`,
  RENTAL_AGREEMENT_DETAIL_REPORT: `/${MAIN_MENU.REPORTS}/rental-agreement-detail-report`,
  ASSET_CATEGORY_PERFORMANCE_REPORT: `/${MAIN_MENU.REPORTS}/asset-category-performance-report`,
  RENTAL_CANCELLATIONS_AMENDMENTS_REPORT: `/${MAIN_MENU.REPORTS}/rental-cancellations-amendments-report`,
  ACTIVE_RENTALS_REPORT: `/${MAIN_MENU.REPORTS}/active-rentals-report`,
  RENTAL_AGREEMENT_REPORT: `/${MAIN_MENU.REPORTS}/rental-agreement-report`,
  UPCOMING_EXPIRY_REPORT: `/${MAIN_MENU.REPORTS}/upcoming-expiry-report`,
  RENTAL_REVENUE_REPORT: `/${MAIN_MENU.REPORTS}/rental-revenue-report`,
  ASSET_AVAILABILITY_REPORT: `/${MAIN_MENU.REPORTS}/asset-availability-report`,
  ASSET_UTILIZATION_REPORT: `/${MAIN_MENU.REPORTS}/asset-utilization-report`,
  CUSTOMER_RENTAL_REPORT: `/${MAIN_MENU.REPORTS}/customer-rental-report`,
};
const itemCrud = generateModulePaths('ITEM', 'ITEM', 'item', 'item');
export const itemsRoutes = {
	// inventory --------------------------------------------------------------------------
	ADD_INVENTORY_ITEM: '/product-management/items/add-inventory-item',
	EDIT_INVENTORY_ITEM: '/product-management/items/:id/edit-inventory-item',
	VIEW_INVENTORY_ITEM: '/product-management/items/:id/view-inventory-item',
	ADD_ASSEMBLY_ITEM: '/product-management/items/add-assembly-item',
	EDIT_ASSEMBLY_ITEM: '/product-management/items/edit-assembly-item',
	VIEW_ASSEMBLY_ITEM: '/product-management/items/view-assembly-item',
	// service --------------------------------------------------------------------------------
	ADD_SERVICE_ITEM: '/product-management/items/add-service-item',
	EDIT_SERVICE_ITEM: '/product-management/items/edit-service-item',
	VIEW_SERVICE_ITEM: '/product-management/items/view-service-item',

}
export const itemCategoryCRUDRoutes = {
  ITEM_CATEGORY: `/${MODULES.CATEGORY}`,
	ADD_ITEM_CATEGORY: '/product-management/item-category/add-item-category',
	EDIT_ITEM_CATEGORY: '/product-management/item-category/:id/edit-item-category',
	VIEW_ITEM_CATEGORY: '/product-management/item-category/:id/view-item-category',
}
export const PathnameRental: Record<any, string> = {
  LANDING_PAGE: '/',
  LIST: '/list',
  ...itemCrud,
  ...itemsRoutes,
  ...itemCategoryCRUDRoutes,
  CATEGORY: `/${MODULES.CATEGORY}`,
   FORMS: "/settings/forms",
  CUSTOM_FORM: "/settings/custom-form",
  DEMAND_PLANNING: `/${MODULES.DEMAND_PLANNING}`,
  DETAILS_DEMAND_PLANNIG: `/${MODULES.DEMAND_PLANNING}/details`,
  INVOICING: `/:id/${MODULES.INVOICING}`,
  INVOICING_RENTAL_ORDERS: `/${MODULES.INVOICING}/rental-orders`,
  INVOICING_PREVIOUS_JOBS: `/${MODULES.INVOICING}/previous-jobs`,
  ...leadPaths,
  ...opportunityPaths,
  ...quotationPaths,
  ...rentalPaths,
  ...rentalDeliveryPaths,
  ...rentalGrnPaths,
  ...agreementsPaths,
  ...rfqPaths,
  ...purchaseOrderPaths,
  ...crossHireRequestPaths,
  ...crossHireProcessPaths,
  ...crossHireRfqPaths,
  ...crossHireOrdersPaths,
  ...crossHireOrdersGrnPaths,
  ...crossHireDeliveryOrdersPaths,
  ...crossHireProcessPaths,
  ...settingPaths,
  ...reportsPaths,
};

export const ROUTES = {
	LIST: PathnameGenerator(PathnameRental.CROSS_HIRE_ORDERS),
	ADD: PathnameGenerator(PathnameRental.ADD_CROSS_HIRE_ORDER),
	EDIT: PathnameGenerator(PathnameRental.EDIT_CROSS_HIRE_ORDER),
	VIEW: PathnameGenerator(PathnameRental.VIEW_CROSS_HIRE_ORDER),
	LIST_GRN: PathnameGenerator(PathnameRental.CROSS_HIRE_ORDERS_GRNS),
	ADD_GRN: PathnameGenerator(PathnameRental.ADD_CROSS_HIRE_ORDER_GRN),
	EDIT_GRN: PathnameGenerator(PathnameRental.EDIT_CROSS_HIRE_ORDER_GRN),
	VIEW_GRN: PathnameGenerator(PathnameRental.VIEW_CROSS_HIRE_ORDER_GRN),
	LIST_DELIVERY_ORDER: PathnameGenerator(PathnameRental.CROSS_HIRE_DELIVERY_ORDERS),
	ADD_DELIVERY_ORDER: PathnameGenerator(PathnameRental.ADD_CROSS_HIRE_DELIVERY_ORDER),
	EDIT_DELIVERY_ORDER: PathnameGenerator(PathnameRental.EDIT_CROSS_HIRE_DELIVERY_ORDER),
	VIEW_DELIVERY_ORDER: PathnameGenerator(PathnameRental.VIEW_CROSS_HIRE_DELIVERY_ORDER),
}

